import { useStudyAuthoringEditor, studyAuthoringQueryPrefix } from './useStudyAuthoringEditor'
import { studyBuilderWireSnapshotSchema, type StudyAuthoringRecord, type StudyBuilderSnapshot } from '@testfeed/shopify-contracts'
import type { NavigationClient } from '../../shopifyNavigation'
import { useShopifyAppNavigation } from '../outside/useShopifyAppNavigation'
import { canonicalJson } from '@testfeed/core'
import { batch } from '@tanstack/store'
import { useStudyBuilderEvaluation } from './useStudyBuilderEvaluation'
import { usePendingEdits } from './usePendingEdits'
import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useForm, useStore } from '@tanstack/react-form'
import type { VerifiedTestFeedConnectionScope } from '../../ShopifyAppContext'
import type { shopifyApiFetch } from '../../shopifyApiFetch'
import type { PageState } from '../../page-state'
import type { ShopifyForkChangeReason } from '../../../src/shared/fork-lineage'
import type { BuilderLoadErrorDictionary } from '../../../src/shared/testfeed-connection-status-contract'
import type { StudySetup } from '../../types'
import { applyStudyBuilderSnapshotToForm } from './studyBuilderActions'
import { useStudyBuilderRunFlow } from './studyBuilderRunFlow'
import { uploadStudyBuilderImage } from '../../studyBuilderImageUpload'
import { useStudyBuilderProductPicker } from './studyBuilderProductPicker'
import { buildStudyBuilderInitialStateQueryKey } from './studyBuilderStatus'
import { type StudyBuilderLifecycle } from './studyBuilderLifecycle'
import type { StudyBuilderStart } from './studyBuilderStart'
import type { PageActionName } from '../../page-declaration'
import type { page } from '../../pages/study-builder.page'

type StudyBuilderOptions = {
  /** The visit this editor belongs to; the page remounts the editor for a new one. */
  visit: string
  scope: VerifiedTestFeedConnectionScope
  /** The connected status; `builderLoadError` carries the Worker's load/offline words (used before any projection exists). */
  readiness: { code: string; builderLoadError?: BuilderLoadErrorDictionary | null } | null
  /** What the form opens with, already decided by the page lifecycle's `initial-state` source. */
  start: StudyBuilderStart
  lifecycle: StudyBuilderLifecycle
  editSetupId: string | null
  draftId: string
  // Fork lineage fields (issue #1143, Phase 1) are set together when reopening a setup as a "New version".
  forkParentPacketId: string | null
  forkChangeReason: ShopifyForkChangeReason | null
  onDraftSaved: (record?: StudyAuthoringRecord) => void
  onStudyPrepared?: (studyId: string) => void
  onSetupSaved?: (setup: StudySetup) => void
}

/** Hooks Shopify Study Builder form state and actions. The page lifecycle owns loading, refresh and errors. */
export function useShopifyStudyBuilder(shopify: Parameters<typeof shopifyApiFetch>[0] & NavigationClient, options: StudyBuilderOptions) {
  const { visit, scope, lifecycle, draftId, onDraftSaved } = options
  const queryClient = useQueryClient()
  const [savedBaseline, setSavedBaseline] = useState(options.start.snapshot)
  const form = useForm({ defaultValues: savedBaseline })
  // While Submit runs, the first save of a brand-new test must not swap the URL (draft → study): that remounts the
  // page and drops the in-flight Submit, which is why it used to take two presses. Submit navigates on success.
  const submitting = useRef(false)
  const pendingUpload = useRef<{ key: string; fileName: string } | null>(null)
  const [upload, setUpload] = useState<{ key: string; fileName: string } | null>(null)
  const acknowledgeSaved = useCallback((saved: StudyAuthoringRecord) => {
    setSavedBaseline(studyBuilderWireSnapshotSchema.parse(saved.draft))
    if (!submitting.current) onDraftSaved(saved)
  }, [onDraftSaved])
  const authoring = useStudyAuthoringEditor(shopify, scope, options.start, draftId, acknowledgeSaved)
  const formDirty = useStore(form.store, (state) => state.isDirty)
  const resetToSnapshot = useCallback((snapshot: StudyBuilderSnapshot) => applyStudyBuilderSnapshotToForm(form, snapshot), [form])

  // Edits change the draft; only an accepted request can resolve its own failure.
  const afterEdit = useCallback(() => {
    lifecycle.changed()
  }, [lifecycle])
  // A brand-new test starts from the server's first answer (it fills in the store's market): until the merchant changes
  // something, that answer is the starting point, not an unsaved change.
  const merchantEdited = useRef(false)
  const applyEvaluation = useCallback((snapshot: StudyBuilderSnapshot) => {
    if (!authoring.record && !merchantEdited.current) {
      setSavedBaseline(snapshot)
      resetToSnapshot(snapshot)
      return
    }
    batch(() => {
      for (const key of Object.keys(snapshot) as (keyof StudyBuilderSnapshot)[]) {
        if (JSON.stringify(form.state.values[key]) !== JSON.stringify(snapshot[key])) {
          // Product facts are server-owned; applying current market prices is not a merchant edit.
          // Selections, comparisons and other authoring fields still update ordinary dirty metadata.
          form.setFieldValue(key, snapshot[key] as never, { dontUpdateMeta: key === 'selectedProduct' })
        }
      }
    })
  }, [form, authoring.record, resetToSnapshot])
  // Unacknowledged edits and an unresolved Save must both protect the current visit.
  const pending = usePendingEdits()
  const readDirty = useCallback(
    () => pendingUpload.current !== null || form.state.isDirty || pending.read().length > 0 || authoring.readUncertainSave(),
    [form, pending.read, authoring.readUncertainSave],
  )
  const navigation = useShopifyAppNavigation({ visit, shopify, readDirty })
  const markEdited = useCallback(() => {
    merchantEdited.current = true
    navigation.edited()
  }, [navigation])
  const evaluation = useStudyBuilderEvaluation({ shopify, form, apply: applyEvaluation, visit, draftId, studyId: authoring.record?.studyId, setupId: null,
    lifecycle, onEdited: afterEdit, onMerchantEdit: markEdited, onAuthoringState: authoring.observeState, pending })
  const handleImageUpload = useCallback(async (key: string, file: File) => {
    return lifecycle.run('upload-image', async (signal) => {
      pendingUpload.current = { key, fileName: file.name }
      setUpload(pendingUpload.current)
      markEdited()
      lifecycle.changed()
      // The page owns cancellation. Stop the evaluation too so an aborted page can never apply a late image.
      const stopEvaluation = () => evaluation.stop()
      signal.addEventListener('abort', stopEvaluation, { once: true })
      try {
        const checked = await evaluation.settleCurrent()
        signal.throwIfAborted()
        if (checked.status !== 'accepted') throw new DOMException('STUDY_BUILDER_UPLOAD_INPUT_CANCELLED', 'AbortError')
        const media = await uploadStudyBuilderImage(shopify, {
          draftId, ...(authoring.record ? { studyId: authoring.record.studyId } : {}), key,
        }, file, signal)
        signal.throwIfAborted()
        const outcome = await evaluation.edit(key, media.mediaUrl)
        signal.throwIfAborted()
        if (outcome.status !== 'accepted') throw new DOMException('STUDY_BUILDER_UPLOAD_CHECK_CANCELLED', 'AbortError')
        if (outcome.result.rejectedEdits.some((edit) => edit.key === key))
          throw new Error('STUDY_BUILDER_UPLOAD_EDIT_REJECTED')
        return media
      } finally {
        signal.removeEventListener('abort', stopEvaluation)
        pendingUpload.current = null
        setUpload(null)
      }
    })
  }, [lifecycle, markEdited, evaluation, shopify, draftId, authoring.record])
  // A later edit, even one the Worker has not applied yet, means the saved values are not the whole draft any more.
  const unchangedSince = useCallback(
    (submitted: StudyBuilderSnapshot) => form.state.values === submitted && pending.read().length === 0,
    [form, pending.read],
  )
  const runFlow = useStudyBuilderRunFlow({
    readCurrent: () => form.state.values,
    settleCurrent: evaluation.settleCurrent,
    lifecycle,
    direct: {
      prepare: authoring.prepare,
      onPrepared: options.onStudyPrepared,
      acknowledge: (saved, submitted) => {
      if (unchangedSince(submitted)) resetToSnapshot(studyBuilderWireSnapshotSchema.parse(saved.draft))
      },
    },
  })
  const saveCurrent = useCallback(async () => {
    const result = await lifecycle.run('save-draft', async (signal) => {
      const checked = await evaluation.settleCurrent()
      if (checked.status !== 'accepted') throw new DOMException('STUDY_BUILDER_SAVE_CHECK_CANCELLED', 'AbortError')
      signal.throwIfAborted()
      const submitted = form.state.values
      if (submitted !== checked.result.snapshot && canonicalJson(submitted) !== canonicalJson(checked.result.snapshot))
        throw new DOMException('STUDY_BUILDER_SAVE_INPUT_CHANGED', 'AbortError')
        const saved = await authoring.persist(submitted, signal)
        signal.throwIfAborted()
      const acknowledged = studyBuilderWireSnapshotSchema.parse(saved.draft)
      setSavedBaseline(acknowledged)
      // Form values are immutable. Only an unchanged form can be reset/marked clean;
      // edits during the request remain on screen and the lifecycle keeps them unsaved.
      if (unchangedSince(submitted)) resetToSnapshot(acknowledged)
    })
    if (result.status === 'done') {
      navigation.settled()
      void queryClient.invalidateQueries({ queryKey: studyAuthoringQueryPrefix(scope) })
      void queryClient.invalidateQueries({ queryKey: buildStudyBuilderInitialStateQueryKey(scope.shopDomain, scope.orgId, scope.connectionId) })
    }
    return result
  }, [authoring, form, evaluation, lifecycle, navigation, queryClient, resetToSnapshot, scope, unchangedSince])
  const handleSaveDraft = useCallback(async () => { await saveCurrent() }, [saveCurrent])
  const handleRunStudyFromBuilder = useCallback(async () => {
    // One press: preparation saves the current form and submits it (authoring.prepare persists first).
    submitting.current = true
    await runFlow.handleRunStudyFromBuilder().finally(() => (submitting.current = false))
  }, [runFlow])
  const handleDiscard = useCallback(async () => {
    const result = await lifecycle.run('discard', async (signal) => {
      const saved = await authoring.reconcile(signal)
      signal.throwIfAborted()
      const baseline = saved ? studyBuilderWireSnapshotSchema.parse(saved.draft) : savedBaseline
      resetToSnapshot(baseline)
      evaluation.discard(baseline)
      // Discard has resolved the uncertain Save and restored its actual baseline.
      // Settle that operation's feedback through the existing token owner too.
        lifecycle.finishFeedback(lifecycle.beginFeedback('save-draft'), 'accepted')
        lifecycle.finishFeedback(lifecycle.beginFeedback('upload-image'), 'accepted')
    })
    if (result.status === 'done') navigation.settled()
    return result.status === 'done'
  }, [authoring, savedBaseline, lifecycle, resetToSnapshot, evaluation, navigation])
  // Only a completed deletion releases unsaved work. Do not call Discard: it reconciles/re-evaluates a now-deleted study.
  const handleDeleted = useCallback(() => {
    evaluation.stop()
    pending.set([])
    resetToSnapshot(form.state.values)
    // Cancel any older leave destination; the completed deletion returns directly to Tests.
    navigation.dispose()
  }, [evaluation, pending, resetToSnapshot, form, navigation])
  // Recovery for a revision conflict on a Save or a Submit (the draft was saved from another tab or by another
  // person): retrying would re-send the same stale revision, so instead pull the latest saved draft and load it.
  // Arm the page first (`requestLoadSaved`) so the refetched draft is applied straight away — replacing the unsaved
  // edits, through the newer-version path (`applyNewerData`) — rather than raising the top-of-page chooser for a
  // second press; the merchant pressing the button is the confirmation. Then refetch the bootstrap, and clear each
  // conflicting operation's feedback through its existing owner (the begin/finish pattern Discard uses to settle an
  // uncertain Save), so the banner by the button clears at once.
  const handleLoadSavedVersion = useCallback(() => {
    lifecycle.requestLoadSaved()
    void queryClient.invalidateQueries({
      queryKey: buildStudyBuilderInitialStateQueryKey(scope.shopDomain, scope.orgId, scope.connectionId),
    })
    for (const failure of lifecycle.feedback)
      if (failure.kind === 'conflict')
        lifecycle.finishFeedback(lifecycle.beginFeedback(failure.operation as PageActionName<typeof page>), 'accepted')
  }, [queryClient, scope, lifecycle])
  const isDirty = upload !== null || formDirty || evaluation.hasPendingEdits || authoring.hasUncertainSave
  const { handleOpenProductPicker } = useStudyBuilderProductPicker({
    shopify, form, lifecycle, onSelectVariant: (value) => evaluation.edit('selectedVariant', value),
  })
  const pageState: PageState = lifecycle.state.status === 'error'
    ? { key: visit, status: 'error', error: lifecycle.state.error.name }
    : lifecycle.state
  return {
    authoring,
    navigation, evaluation,
    pageState,
    visit,
    handleRunStudyFromBuilder,
    // The Worker owns the per-status edit locks; the app only ORs them with its own in-flight interaction locks.
    permissions: authoring.state
      ? {
          textDisabled: authoring.state.locks.text || lifecycle.interactions.textDisabled,
          structureDisabled: authoring.state.locks.structure || lifecycle.interactions.structureDisabled,
        }
      : lifecycle.interactions,
    // A failure may now carry no server message (a network blip), so the form-error gate reads PRESENCE, not wording:
    // the panel renders the request-failure notice (which sources its own words) whenever a failure exists.
    requestFailures: lifecycle.feedback, formError: lifecycle.feedback.length > 0 ? true : null, lifecycle,
    // Exposed so the request-failure notice can show the Worker's offline words before any evaluation projection exists.
    readiness: options.readiness,
    handleOpenProductPicker, handleImageUpload, upload,
    edit: (key: string, value: string, pace?: 'now' | 'typing') => { void evaluation.edit(key, value, pace) },
    isDirty, readDirty, handleDiscard, handleDeleted, handleSaveDraft, handleLoadSavedVersion,
  }
}
